/**
 * KPI 2.0 (스마트제조 지원시스템 2.0) 어드민 타입 정의
 *
 * 백엔드 컨트롤러: AdminKpiProgramController (`/api/admin/kpi`, ADMIN 전용)
 * 백엔드 DTO: presentation/dtos/{request,response}/kpi2/*
 *
 * 구조
 *  - ① 회사 KPI 전역 설정 (companies.kpi_*)        → CompanyKpi2Settings
 *  - ② 카테고리(공방/공장) = kpi_program          → KpiProgram
 *  - ③ 슬롯(보고 KPI 항목) = sbiz24_config        → KpiSlot
 *  - ④ 보고 현황(요약/캘린더/로그)                → KpiReportSummary / KpiCalendarCell / KpiReportLog
 *  - ⑤ 수동 보고                                  → ManualReport*
 */

// ==================== Enum 타입 (백엔드 enum 과 동일) ====================

/** 사업 코드 — 백엔드 enumeration.kpi2.PluginType (구 ProgramType). */
export enum PluginType {
  /** 스마트 공방 (정부 보고, 인증키 필요) */
  SMART_WORKSHOP = "SMART_WORKSHOP",
  /** 스마트 공장 (MES 전용) */
  SMART_FACTORY = "SMART_FACTORY",
  /** 지원사업 고지 문구 (KPI·보고 없음. 회사 헤더 성과물 배너) */
  ACHIEVEMENT_NOTICE = "ACHIEVEMENT_NOTICE",
}

/** sbiz24 집계 주기 — 백엔드 enumeration.kpi2.AggregationInterval */
export enum AggregationInterval {
  /** 건 단위 */
  PER_RECORD = "PER_RECORD",
  /** 분 단위 */
  MINUTELY = "MINUTELY",
  /** 시간 단위 */
  HOURLY = "HOURLY",
  /** 일 단위 */
  DAILY = "DAILY",
  /** 주 단위 */
  WEEKLY = "WEEKLY",
  /** 월 단위 */
  MONTHLY = "MONTHLY",
}

/** 정부 보고용 sbiz24 항목 코드 — 백엔드 enumeration.kpi2.SbizType (SMART_WORKSHOP 전용) */
export enum SbizType {
  /** 가동 건수 (생산량 — 동작수) P/B */
  OPERATION_COUNT = "OPERATION_COUNT",
  /** 생산량 (통합 — 호환용) P/B */
  OUTPUT_QUANTITY = "OUTPUT_QUANTITY",
  /** 매엽지 생산량 P/B (포스터/명함/전단지/스티커/걸이배너/스탠드배너) */
  SHEET_PRODUCTION = "SHEET_PRODUCTION",
  /** 책자 생산량 P/B (소프트커버/하드커버) */
  BOOK_PRODUCTION = "BOOK_PRODUCTION",
  /** 가동률 (%) P/A */
  OPERATION_RATE = "OPERATION_RATE",
  /** 수주-출하 리드타임 D/C */
  ORDER_SHIPMENT_LEAD_TIME = "ORDER_SHIPMENT_LEAD_TIME",
  /** 납기 준수율 (%) D/G */
  DELIVERY_COMPLIANCE_RATE = "DELIVERY_COMPLIANCE_RATE",
  /** 수정(설계변경) 감소 Q/J */
  REVISION_REDUCTION = "REVISION_REDUCTION",
}

/**
 * MES 지표 종류 — 백엔드 enumeration.kpi2.KpiType (SMART_FACTORY 전용, 정부 보고 없음).
 * SbizType 과 명칭이 겹치는 항목도 있으나 매핑 단위가 다르다(가동률→가동시간, 납기준수율→납기준수시간).
 */
export enum KpiType {
  OPERATION_COUNT = "OPERATION_COUNT",
  OUTPUT_QUANTITY = "OUTPUT_QUANTITY",
  SHEET_PRODUCTION = "SHEET_PRODUCTION",
  BOOK_PRODUCTION = "BOOK_PRODUCTION",
  OPERATING_TIME = "OPERATING_TIME",
  ORDER_SHIPMENT_LEAD_TIME = "ORDER_SHIPMENT_LEAD_TIME",
  DELIVERY_COMPLIANCE_TIME = "DELIVERY_COMPLIANCE_TIME",
  REVISION_REDUCTION = "REVISION_REDUCTION",
}

/** KpiType raw 표시 라벨 — 백엔드 KpiType.label 과 동일(정부 보고 문구 아님). */
export const KPI_TYPE_LABELS: Record<KpiType, string> = {
  [KpiType.OPERATION_COUNT]: "가동 건수",
  [KpiType.OUTPUT_QUANTITY]: "생산량",
  [KpiType.SHEET_PRODUCTION]: "매엽지 생산량",
  [KpiType.BOOK_PRODUCTION]: "책자 생산량",
  [KpiType.OPERATING_TIME]: "가동 시간",
  [KpiType.ORDER_SHIPMENT_LEAD_TIME]: "수주출하 리드타임",
  [KpiType.DELIVERY_COMPLIANCE_TIME]: "납기 준수 시간",
  [KpiType.REVISION_REDUCTION]: "수정 감소",
};

/** KPI1(생산성)/KPI2(납기·품질) 그룹 — 백엔드 KpiType.group 과 동일 배정. */
export const KPI1_TYPES = new Set<KpiType>([
  KpiType.OPERATION_COUNT,
  KpiType.OUTPUT_QUANTITY,
  KpiType.SHEET_PRODUCTION,
  KpiType.BOOK_PRODUCTION,
  KpiType.OPERATING_TIME,
]);
export const isKpi1Type = (t: KpiType): boolean => KPI1_TYPES.has(t);

/**
 * 수동 보고 타겟 — 백엔드 ManualReportTarget (CategoryContent [5] "보고 항목" select 와 1:1)
 */
export enum ManualReportTarget {
  /** 가동 Y/N 재송신 (LEVEL1) */
  LEVEL1 = "LEVEL1",
  /** KPI1(P 그룹) 슬롯 LEVEL2 + LEVEL3 */
  KPI1_ALL = "KPI1_ALL",
  /** KPI1(P 그룹) 슬롯 LEVEL2 만 */
  KPI1_LEVEL2 = "KPI1_LEVEL2",
  /** KPI1(P 그룹) 슬롯 LEVEL3 만 */
  KPI1_LEVEL3 = "KPI1_LEVEL3",
  /** KPI2(D·Q 그룹) 슬롯 LEVEL2 + LEVEL3 */
  KPI2_ALL = "KPI2_ALL",
  /** KPI2(D·Q 그룹) 슬롯 LEVEL2 만 */
  KPI2_LEVEL2 = "KPI2_LEVEL2",
  /** KPI2(D·Q 그룹) 슬롯 LEVEL3 만 */
  KPI2_LEVEL3 = "KPI2_LEVEL3",
}

/** 보고 캘린더 셀 상태 — 백엔드 KpiReportCalendarCellResponse.status */
export type KpiCalendarStatus = "SUCCESS" | "FAILURE" | "PENDING" | "EMPTY";

/** 보고 로그 레벨 — 백엔드 KpiReportLogResponse.level */
export type KpiReportLogLevel = "LEVEL1" | "LEVEL2" | "LEVEL3";

/** 보고 로그 상태 — 백엔드 KpiReportLogResponse.status */
export type KpiReportLogStatus = "SUCCESS" | "CALCULATION_FAILED";

// ==================== ① 회사 KPI 전역 설정 ====================

/**
 * KPI 2.0 전역 설정 — 백엔드 CompanyKpi2SettingsResponse.
 * SMART_WORKSHOP 플러그인(사업 상세 sbiz24_detail) 단위. companyId 가 아니라 pluginId 로 조회/수정한다
 * (플러그인 가입 = sbiz24_detail 존재가 전제이므로 pluginId 만으로 충분 — 미가입 케이스가 구조적으로 없음).
 */
export interface CompanyKpi2Settings {
  pluginId: number;
  kpiEnabled: boolean;
  kpiTestMode: boolean;
  kpiAutoReport: boolean;
}

/**
 * 회사 KPI 전역 설정 부분 수정 요청 (PATCH) — 백엔드 UpdateCompanyKpi2SettingsCommand.
 * null/미지정 필드는 기존 값 유지.
 */
export interface UpdateCompanyKpi2SettingsRequest {
  kpiEnabled?: boolean | null;
  kpiTestMode?: boolean | null;
  kpiAutoReport?: boolean | null;
}

// ==================== ② 가입 사업 = plugins ====================

/** 가입 사업(plugin) — 백엔드 PluginResponse. id 는 pluginId(슬롯/보고 API 키). */
export interface Plugin {
  /** 플러그인 id (슬롯/보고/수동 API 의 키) */
  id: number;
  companyId: number;
  /** 사업 종류 (SMART_WORKSHOP / SMART_FACTORY / ACHIEVEMENT_NOTICE) */
  pluginType: PluginType;
  /** 사업 시작 일자 (yyyy-MM-dd) */
  startDate: string;
  /** 만료 일자 (yyyy-MM-dd) */
  endDate: string;
  /** 대표장비 machineId — 슬롯 측정 장비 중 하나, 미지정 null (SMART_WORKSHOP/SMART_FACTORY 공통) */
  representativeMachineId: number | null;
  /**
   * 인증키 원문 (SMART_WORKSHOP/SMART_FACTORY). 가입 시 최초 1회만 입력되고 이후 수정 API가 없어
   * 조회 전용으로 그대로 노출한다 — null 이면 미등록(정상적으로는 발생하지 않음, 둘 다 가입 시 필수).
   */
  certKey: string | null;
  /** 하위 슬롯(sbiz24_config) 수 (SMART_WORKSHOP/SMART_FACTORY 공용) */
  slotCount: number;
  /** 지원사업 고지 문구 (ACHIEVEMENT_NOTICE 전용, 그 외 null) */
  disclosureText: string | null;
  /** 로그 API 전송 활성화 (SMART_FACTORY, 그 외 false) */
  factoryLogApiEnabled: boolean;
  /** 회사별 테스트 모드 (SMART_FACTORY, 그 외 false) */
  factoryTestMode: boolean;
  /** 1시간 무활동 시 더미 로그 자동 전송 (SMART_FACTORY, 그 외 false) */
  factoryAutoFallback: boolean;
}

/**
 * 사업 참여 추가 요청 — 백엔드 CreatePluginCommand.
 * 인증키 필요 사업(SMART_WORKSHOP/SMART_FACTORY 둘 다)은 certKey 필수 — 가입 시 최초 1회만 입력 가능하고
 * 이후 수정 API가 없다(오입력 시 플러그인 해제 후 재가입).
 */
export interface CreatePluginRequest {
  companyId: number;
  /** 참여할 사업 코드 (SMART_WORKSHOP / SMART_FACTORY) */
  pluginType: PluginType;
  /** 보고 활성 시작 일자 (yyyy-MM-dd) */
  startDate: string;
  /** 만료 일자 (yyyy-MM-dd) */
  endDate: string;
  /** 인증키 (SMART_WORKSHOP/SMART_FACTORY 필수). sbiz24_detail/factory_detail.cert_key 에 저장 */
  certKey?: string | null;
  /** 지원사업 고지 문구 (ACHIEVEMENT_NOTICE 사업 전용) */
  disclosureText?: string | null;
}

/**
 * 사업 참여 기간·공지문구·SMART_FACTORY 설정 수정 요청 — 백엔드 UpdatePluginCommand.
 * null/미지정 필드는 기존 값 유지. 인증키는 여기 없음 — 가입 시 최초 입력 후 잠금(수정 API 자체가 없음).
 */
export interface UpdatePluginRequest {
  /** 보고 활성 시작 일자 (yyyy-MM-dd) */
  startDate?: string | null;
  /** 만료 일자 (yyyy-MM-dd) */
  endDate?: string | null;
  /** 지원사업 고지 문구 (ACHIEVEMENT_NOTICE). null/미지정이면 미변경 */
  disclosureText?: string | null;
  /** true 면 고지 문구를 NULL 로 비움(연동 해제) */
  clearDisclosure?: boolean;
  /** 로그 API 전송 활성화 (SMART_FACTORY). null/미지정이면 미변경 */
  factoryLogApiEnabled?: boolean | null;
  /** 회사별 테스트 모드 (SMART_FACTORY). null/미지정이면 미변경 */
  factoryTestMode?: boolean | null;
  /** 1시간 무활동 시 더미 로그 자동 전송 (SMART_FACTORY). null/미지정이면 미변경 */
  factoryAutoFallback?: boolean | null;
}

/**
 * 지원사업 고지 문구 자동채움 템플릿 — 기존 하드코딩 문구. 연도는 현재 연도로 채워진다.
 * 코드(No.)는 회사·연도마다 다르므로 placeholder 로 두고 입력받는다.
 */
export const DISCLOSURE_TEMPLATES: {
  label: string;
  build: (year: number) => string;
}[] = [
  {
    label: "혁신바우처 사업",
    build: (y) =>
      `본 프로그램(인쇄 통합관리시스템)은 중소벤처기업부에서 지원하는 ${y}년도 중소기업 혁신바우처 사업(No. B20250819-26719)으로 인한 성과물임을 밝힙니다.\nThis work (Code No. B20250819-26719) was supported by Business for SMB Innovation Voucher funded by Korea Small and Medium Business Administration in ${y}.`,
  },
  {
    label: "스마트제조지원사업",
    build: (y) =>
      `본 프로그램은 소상공인진흥공단에서 지원하는 ${y}년 스마트제조지원사업으로 인한 성과물임을 밝힙니다.`,
  },
];

// ==================== ③ 슬롯 = sbiz24_config ====================

/**
 * 슬롯 — 백엔드 KpiSlotResponse. SMART_WORKSHOP(sbiz24_config) / SMART_FACTORY(factory_config) 공통.
 * sbizType/정부 코드는 WORKSHOP 만 값, kpiType 은 항상 값(FACTORY 는 네이티브, WORKSHOP 은 환산값).
 * 그룹 판정: WORKSHOP 은 kpiFldCd(P=KPI1, D·Q=KPI2), FACTORY 는 kpiType + isKpi1Type().
 */
export interface KpiSlot {
  id: number;
  companyId: number;
  /** 소속 플러그인(SMART_WORKSHOP / SMART_FACTORY) id */
  pluginId: number;
  /** 정부 보고용 sbiz24 항목 코드 — SMART_WORKSHOP 만 값, SMART_FACTORY 는 null */
  sbizType: SbizType | null;
  /** MES 지표 종류 — 항상 값 (SMART_WORKSHOP 은 sbizType 환산값) */
  kpiType: KpiType;
  /** 도입 전 기존 수치 (백엔드 BigDecimal → number) */
  baseValue: number;
  /** 도입 후 목표 수치 (백엔드 BigDecimal → number) */
  goalValue: number;
  unit: string;
  aggregationInterval: AggregationInterval;
  /** KPI 분야 코드 (P=생산성→KPI1, D=납기/Q=품질→KPI2) — SMART_WORKSHOP 만 값 */
  kpiFldCd: string | null;
  /** KPI 상세 코드 — SMART_WORKSHOP 만 값 */
  kpiDtlCd: string | null;
  /** KPI 상세 명칭 (정부 보고 표기) — SMART_WORKSHOP 만 값 */
  kpiDtlNm: string | null;
  /** 측정 대상 장비(합산) 목록 */
  machines: KpiSlotMachine[];
  /** 가동률 슬롯의 하루 가동 목표 시간(분) — % 환산 분모. 그 외 null */
  dailyTargetMinutes: number | null;
  createdAt: string;
  updatedAt: string | null;
}

/** 슬롯의 측정 장비 1대. */
export interface KpiSlotMachine {
  id: number;
  name: string;
  /** 장비 타입 (PRINTER/PLOTTER/CUTTER/BINDER 등) */
  machineType: string;
  ipAddress: string;
  /** 이 장비가 회사(사업) 대표장비인지 — Plugin.representativeMachineId 와 동일 소스(company-wide) */
  isRepresentative: boolean;
}

/**
 * 슬롯 추가 요청 — 백엔드 CreateKpiSlotCommand.
 * sbizType(SMART_WORKSHOP) / kpiType(SMART_FACTORY) 중 플러그인 타입에 맞는 쪽만 채운다
 * (반대쪽 채우면 400).
 */
export interface CreateKpiSlotRequest {
  sbizType?: SbizType | null;
  kpiType?: KpiType | null;
  /** 도입 전 기존 수치 */
  baseValue: number;
  /** 도입 후 목표 수치 */
  goalValue: number;
  unit: string;
  aggregationInterval: AggregationInterval;
  /** 측정 대상 장비(합산). 비면 대표장비 폴백 */
  machineIds: number[];
  /** 가동률 하루 가동 목표 시간(분). 그 외 null */
  dailyTargetMinutes?: number | null;
}

/**
 * 슬롯 수정 요청 (편집 모드 저장) — 백엔드 UpdateKpiSlotCommand.
 * sbizType 은 변경 불가. null/미지정 필드는 기존 값 유지.
 */
export interface UpdateKpiSlotRequest {
  baseValue?: number | null;
  goalValue?: number | null;
  unit?: string | null;
  aggregationInterval?: AggregationInterval | null;
  /** 측정 대상 장비(합산). null/미지정=미변경, 제공 시 전체 교체 */
  machineIds?: number[] | null;
  /** 가동률 하루 가동 목표 시간(분). null/미지정=미변경 */
  dailyTargetMinutes?: number | null;
}

// ==================== ④ 보고 현황 (요약/캘린더/로그) ====================

/** 요약 카드 4종 — 백엔드 KpiReportSummaryResponse */
export interface KpiReportSummary {
  keyStatus: {
    /** 인증키 상태 라벨 (유효 / 만료 임박 / 만료 / 미등록) */
    label: string;
    /** 점 색상 */
    dotColor: string;
  };
  /** 만료까지 남은 일수 텍스트 (예 "325 일") */
  daysUntilExpire: string;
  todayLevel1: {
    /** 오늘 LEVEL1 가동유무. 기록 없으면 "N". */
    badge: string;
    /** 송신 시각 텍스트 (예 "14:22:01 전송"). 없으면 "—". */
    reportedAt: string;
  };
  /** 금월 보고 성공 일수 / 보고 대상 일수 텍스트 (예 "21 / 21 일 (100%)") */
  monthlySuccess: string;
}

/**
 * 보고 캘린더 셀 — 백엔드 KpiReportCalendarCellResponse.
 * 응답은 해당 월 1일~말일까지의 실제 일자(1..말일) 셀 리스트.
 * (이전 달 placeholder 는 프론트가 직접 채운다.)
 */
export interface KpiCalendarCell {
  day: number;
  status: KpiCalendarStatus;
}

/** 최근 보고 로그 행 — 백엔드 KpiReportLogResponse */
export interface KpiReportLog {
  /** 송신 시각 (백엔드에서 포맷된 문자열) */
  transmittedAt: string;
  level: KpiReportLogLevel;
  /** 항목 표기 (LEVEL1="가동유무 (Y/N)", LEVEL2/3=지표명 + 코드) */
  item: string;
  /** 측정값 텍스트 (LEVEL1 또는 미계산 시 "—") */
  measuredValue: string;
  /** 성과율 텍스트 (LEVEL2 성과율, 없으면 "—") */
  performanceRate: string;
  status: KpiReportLogStatus;
}

/** 페이징 응답 (백엔드 PageResponse 와 동일) */
export interface KpiPageResponse<T> {
  items: T[];
  totalPages: number;
  totalElements: number;
  pageSize: number;
  next: string | null;
  previous: string | null;
}

/** 스마트공장 로그 API 전송 이력 useSe 코드 — 백엔드 FactoryLogUseSe */
export type FactoryLogUseSe = "DO6001" | "DO6004" | "DO6005" | "DO6006";

/** 스마트공장 로그 전송 상태 — 백엔드 FactoryLogStatus */
export type FactoryLogStatus = "SHADOW" | "SUCCESS" | "TRANSMISSION_FAILED";

/** 스마트공장 로그 API 전송 이력 1건 — 백엔드 FactoryLogResponse */
export interface FactoryLog {
  id: number;
  /** 전송 payload 그대로 (YYYY-MM-DD HH:MI:SS.SSS) */
  logDt: string;
  useSe: FactoryLogUseSe;
  conectIp: string;
  dataUsgqty: number;
  status: FactoryLogStatus;
  /** AP1002 등 정부 응답 코드 (shadow/실패 시 null 일 수 있음) */
  recptnRsltCd: string | null;
  transmittedAt: string | null;
  createdAt: string;
}

/** useSe 코드 표시 라벨 — 백엔드에서 내려주지 않아 프론트에서 매핑 */
export const FACTORY_LOG_USE_SE_LABELS: Record<FactoryLogUseSe, string> = {
  DO6001: "로그인",
  DO6004: "데이터 등록",
  DO6005: "데이터 수정",
  DO6006: "데이터 삭제",
};

// ==================== ⑤ 수동 보고 ====================

/**
 * 수동 보고 요청 — 백엔드 ManualReportCommand.
 * 지정한 과거 일자의 KPI 를 재계산하여 송신(누락 복구용).
 */
export interface ManualReportRequest {
  /** 보고 대상 일자 (과거일 yyyy-MM-dd) */
  date: string;
  target: ManualReportTarget;
}

/** 수동 보고 결과 — 백엔드 ManualReportResponse */
export interface ManualReportResult {
  /** 요청 타겟 (LEVEL1 / KPI1_* / KPI2_*) */
  target: string;
  /** 보고 대상 일자 (yyyy-MM-dd) */
  date: string;
  /** shadow-mode 여부 */
  shadow: boolean;
  /** 성공(또는 SHADOW) 처리 건수 */
  successCount: number;
  /** 실패(송신 실패) 처리 건수 */
  failureCount: number;
  /** 전체 결과 요약 메시지 */
  message: string;
  details: ManualReportDetail[];
}

/** 수동 보고 대상별 처리 상세 — 백엔드 ManualReportResponse.Detail */
export interface ManualReportDetail {
  /** 처리 레벨 ("LEVEL1" / "LEVEL2/3") */
  level: string;
  /** 대상 sbizType (LEVEL1 은 null) */
  sbizType: string | null;
  /** 처리 상태 (SUCCESS / TRANSMISSION_FAILED / SHADOW / SKIPPED 등) */
  status: string;
  message: string;
}

// ==================== 표시용 라벨/색상 상수 ====================

export const PLUGIN_TYPE_LABELS: Record<PluginType, string> = {
  [PluginType.SMART_WORKSHOP]: "스마트 공방",
  [PluginType.SMART_FACTORY]: "스마트 공장",
  [PluginType.ACHIEVEMENT_NOTICE]: "지원사업 고지 문구",
};

export const AGGREGATION_INTERVAL_LABELS: Record<AggregationInterval, string> = {
  [AggregationInterval.PER_RECORD]: "건 단위",
  [AggregationInterval.MINUTELY]: "분 단위",
  [AggregationInterval.HOURLY]: "시간 단위",
  [AggregationInterval.DAILY]: "일 단위",
  [AggregationInterval.WEEKLY]: "주 단위",
  [AggregationInterval.MONTHLY]: "월 단위",
};

export const SBIZ_TYPE_LABELS: Record<SbizType, string> = {
  [SbizType.OPERATION_COUNT]: "가동 건수",
  [SbizType.OUTPUT_QUANTITY]: "생산량 증가",
  [SbizType.SHEET_PRODUCTION]: "생산량 증가(매)",
  [SbizType.BOOK_PRODUCTION]: "생산량 증가(권)",
  [SbizType.OPERATION_RATE]: "가동률 증가",
  [SbizType.ORDER_SHIPMENT_LEAD_TIME]: "수주출하 리드타임 단축",
  [SbizType.DELIVERY_COMPLIANCE_RATE]: "납기 준수율",
  [SbizType.REVISION_REDUCTION]: "설계변경 건수 감소",
};